import * as fabric from 'fabric';

// ─── 마커 커스텀 렌더링 ───

const drawMarker = (ctx, x, y, angle, type, size, color) => {
  if (type === 'none') return;
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(angle);
  ctx.fillStyle = color;
  ctx.beginPath();

  if (type === 'arrow') {
    ctx.moveTo(0, -size);
    ctx.lineTo(size * 1.5, 0);
    ctx.lineTo(0, size);
    ctx.closePath();
  } else if (type === 'circle') {
    ctx.arc(0, 0, size, 0, Math.PI * 2);
  } else if (type === 'diamond') {
    ctx.moveTo(-size, 0);
    ctx.lineTo(0, -size);
    ctx.lineTo(size, 0);
    ctx.lineTo(0, size);
    ctx.closePath();
  } else if (type === 'square') {
    ctx.rect(-size, -size, size * 2, size * 2);
  }

  ctx.fill();
  ctx.restore();
};

const applyMarkerRendering = (lineObj) => {
  const originalRender = lineObj._render.bind(lineObj);
  lineObj._render = function (ctx) {
    originalRender(ctx);

    const marker = this._marker;
    if (!marker || marker === 'none') return;

    const lp = this.calcLinePoints();
    const dx = lp.x2 - lp.x1;
    const dy = lp.y2 - lp.y1;
    const angle = Math.atan2(dy, dx);
    const size = Math.max(this.strokeWidth * 1.5, 6);
    const color = this.stroke || '#000000';

    drawMarker(ctx, lp.x1, lp.y1, angle + Math.PI, marker, size, color);
    drawMarker(ctx, lp.x2, lp.y2, angle, marker, size, color);
  };
};

// ─── 커스텀 컨트롤: 라인 끝점 ───

const CONTROL_SIZE = 12;

const renderEndpointControl = (ctx, left, top) => {
  ctx.save();
  ctx.beginPath();
  ctx.arc(left, top, CONTROL_SIZE / 2, 0, Math.PI * 2);
  ctx.fillStyle = '#ffffff';
  ctx.fill();
  ctx.strokeStyle = '#3b82f6';
  ctx.lineWidth = 2;
  ctx.stroke();
  ctx.restore();
};

/**
 * positionHandler:
 * Fabric.js polyControl과 동일한 패턴 사용
 * calcLinePoints()는 객체 로컬 좌표 (중심=0,0)
 * vpt * objectMatrix로 직접 변환 → 스크린 좌표
 */
const makePositionHandler = (which) => {
  return function (dim, finalMatrix, fabricObject) {
    const lp = fabricObject.calcLinePoints();
    const lpX = which === 'start' ? lp.x1 : lp.x2;
    const lpY = which === 'start' ? lp.y1 : lp.y2;

    const matrix = fabric.util.multiplyTransformMatrices(
      fabricObject.getViewportTransform(),
      fabricObject.calcTransformMatrix(),
    );
    return new fabric.Point(lpX, lpY).transform(matrix);
  };
};

/**
 * actionHandler: 끝점 드래그
 * (x, y)는 scene 좌표 (세계 좌표)
 */
const makeEndpointActionHandler = (which) => {
  return (eventData, transform, x, y) => {
    const target = transform.target;

    // 이동 후 x1/y1/x2/y2가 left/top과 동기화되지 않은 상태를 보정
    // Fabric.js는 드래그 이동 시 left/top만 갱신하고 x1/y1/x2/y2는 그대로 유지함
    const offsetX = target.left - (target.x1 + target.x2) / 2;
    const offsetY = target.top - (target.y1 + target.y2) / 2;
    if (offsetX !== 0 || offsetY !== 0) {
      target.x1 += offsetX;
      target.y1 += offsetY;
      target.x2 += offsetX;
      target.y2 += offsetY;
    }

    if (which === 'start') {
      target.x1 = x;
      target.y1 = y;
    } else {
      target.x2 = x;
      target.y2 = y;
    }

    target.width = Math.abs(target.x2 - target.x1);
    target.height = Math.abs(target.y2 - target.y1);
    target.left = (target.x1 + target.x2) / 2;
    target.top = (target.y1 + target.y2) / 2;

    target.dirty = true;
    target.setCoords();
    if (target.canvas) target.canvas.requestRenderAll();
    return true;
  };
};

/** fabric.Line에 커스텀 끝점 컨트롤 + 마커 렌더링 적용 */
export const applyLineControls = (lineObj) => {
  lineObj.set({
    hasBorders: false,
    cornerSize: CONTROL_SIZE,
    transparentCorners: false,
    objectCaching: false,
    padding: Math.max(lineObj.strokeWidth * 2, 15),
  });

  lineObj.setControlsVisibility({
    tl: false, tr: false, bl: false, br: false,
    ml: false, mr: false, mt: false, mb: false,
    mtr: false,
  });

  lineObj.controls.start = new fabric.Control({
    positionHandler: makePositionHandler('start'),
    actionHandler: makeEndpointActionHandler('start'),
    cursorStyleHandler: () => 'crosshair',
    render: renderEndpointControl,
    actionName: 'modifyLine',
  });

  lineObj.controls.end = new fabric.Control({
    positionHandler: makePositionHandler('end'),
    actionHandler: makeEndpointActionHandler('end'),
    cursorStyleHandler: () => 'crosshair',
    render: renderEndpointControl,
    actionName: 'modifyLine',
  });

  applyMarkerRendering(lineObj);
};

// ─── 좌표 추출 ───

export const extractLineCoords = (lineObj) => ({
  start: { x: lineObj.x1, y: lineObj.y1 },
  end: { x: lineObj.x2, y: lineObj.y2 },
});

// ─── 라인 생성 ───

export const createLineObject = (canvas, start, end, settings) => {
  const { color, width, opacity, strokeDashArray, marker } = settings;

  const line = new fabric.Line([start.x, start.y, end.x, end.y], {
    id: `line-${Date.now()}`,
    name: 'Line',
    stroke: color,
    strokeWidth: width,
    strokeDashArray: strokeDashArray || null,
    opacity,
    _marker: marker || 'none',
  });

  applyLineControls(line);
  canvas.add(line);
  return line;
};

// ─── 라인 속성 업데이트 ───

export const updateLineSettings = (canvas, lineObj, settings) => {
  if (settings.marker !== undefined) lineObj._marker = settings.marker;
  if (settings.color !== undefined) lineObj.set({ stroke: settings.color });
  if (settings.width !== undefined) {
    lineObj.set({ strokeWidth: settings.width });
    lineObj.set({ padding: Math.max(settings.width * 2, 15) });
  }
  if (settings.opacity !== undefined) lineObj.set({ opacity: settings.opacity });
  if (settings.strokeDashArray !== undefined) lineObj.set({ strokeDashArray: settings.strokeDashArray });

  lineObj.dirty = true;
  canvas.requestRenderAll();
  return lineObj;
};
